import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Dice5, RotateCcw, Share2, Plus, Minus } from "lucide-react";
import { EMBED } from "@/constants/testIds";

/**
 * Jixxeo Dice — rolls up to 6 d6 with a shake animation. Web Audio blip on
 * roll. Keeps a running history.
 */

const FACES = [
  // pip positions (grid 3x3 centers)
  { pips: [4] },
  { pips: [0, 8] },
  { pips: [0, 4, 8] },
  { pips: [0, 2, 6, 8] },
  { pips: [0, 2, 4, 6, 8] },
  { pips: [0, 2, 3, 5, 6, 8] },
];

const DIE_COLORS = ["#00F0FF", "#39FF14", "#FFE300", "#FF003C", "#8A2BE2", "#FF8C00"];

function Pip({ visible, color }) {
  return (
    <span className="rounded-full" style={{
      width: 10, height: 10,
      background: visible ? color : "transparent",
      boxShadow: visible ? `0 0 8px ${color}` : "none",
    }} />
  );
}

function Die({ value, color, spinning }) {
  const face = FACES[Math.max(1, Math.min(6, value)) - 1];
  return (
    <motion.div
      key={`${value}-${spinning}`}
      initial={{ rotate: spinning ? 0 : 0, scale: spinning ? 0.9 : 1 }}
      animate={{ rotate: spinning ? 360 : 0, scale: 1 }}
      transition={{ duration: spinning ? 0.6 : 0.2 }}
      className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border-2 relative"
      style={{
        background: `linear-gradient(135deg, ${color}22, ${color}05)`,
        borderColor: `${color}88`,
        boxShadow: `0 0 24px ${color}44, inset 0 0 24px ${color}11`,
      }}>
      <div className="absolute inset-3 grid grid-cols-3 grid-rows-3 gap-1 items-center justify-items-center">
        {Array.from({ length: 9 }).map((_, i) => (
          <Pip key={i} visible={face.pips.includes(i)} color={color} />
        ))}
      </div>
    </motion.div>
  );
}

export default function JixxeoDice() {
  const [count, setCount] = useState(2);
  const [values, setValues] = useState([1, 1]);
  const [spinning, setSpinning] = useState(false);
  const [history, setHistory] = useState([]);
  const ctxRef = useRef(null);

  const beep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!ctxRef.current) ctxRef.current = new AC();
      const ctx = ctxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(160, ctx.currentTime + 0.25);
      g.gain.setValueAtTime(0.06, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.3);
    } catch {}
  };

  const roll = () => {
    if (spinning) return;
    setSpinning(true);
    beep();
    // rapid flicker
    let ticks = 0;
    const iv = setInterval(() => {
      setValues(Array.from({ length: count }, () => 1 + Math.floor(Math.random() * 6)));
      ticks++;
      if (ticks >= 8) {
        clearInterval(iv);
        const final = Array.from({ length: count }, () => 1 + Math.floor(Math.random() * 6));
        setValues(final);
        setHistory((h) => [{ id: Date.now(), values: final, total: final.reduce((a, b) => a + b, 0) }, ...h].slice(0, 12));
        setSpinning(false);
      }
    }, 65);
  };

  const setCountBounded = (n) => {
    const nn = Math.max(1, Math.min(6, n));
    setCount(nn);
    setValues((prev) => {
      if (prev.length === nn) return prev;
      if (prev.length < nn) return [...prev, ...Array.from({ length: nn - prev.length }, () => 1)];
      return prev.slice(0, nn);
    });
  };

  const total = values.reduce((a, b) => a + b, 0);

  const share = async () => {
    const txt = `Jixxeo Dice · ${values.join(" · ")} = ${total}`;
    try {
      if (navigator.share) await navigator.share({ title: "Jixxeo Dice", text: txt });
      else await navigator.clipboard.writeText(txt);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        background: "radial-gradient(ellipse at 30% 30%, rgba(0,240,255,0.15), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(57,255,20,0.10), transparent 60%)",
      }} />
      <div className="relative max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl bg-[#39FF14] flex items-center justify-center shadow-[0_0_24px_rgba(57,255,20,0.5)]">
            <Dice5 className="w-6 h-6 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
            <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none">Jixxeo Dice</h1>
          </div>
          <button onClick={share}
            className="ml-auto w-10 h-10 rounded-full border border-white/15 hover:border-[#00F0FF]/60 flex items-center justify-center text-white/70">
            <Share2 className="w-4 h-4" />
          </button>
        </div>

        <div className="glass rounded-3xl p-6 md:p-8 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setCountBounded(count - 1)}
              className="w-9 h-9 rounded-full border border-white/15 hover:border-[#39FF14]/50 flex items-center justify-center">
              <Minus className="w-4 h-4" />
            </button>
            <div className="mono text-xs uppercase tracking-widest text-white/60">{count} × d6</div>
            <button onClick={() => setCountBounded(count + 1)}
              className="w-9 h-9 rounded-full border border-white/15 hover:border-[#39FF14]/50 flex items-center justify-center">
              <Plus className="w-4 h-4" />
            </button>
            <div className="ml-auto mono text-xs text-white/60">
              Total: <span className="font-heading font-black text-2xl text-[#FFE300] ml-1">{total}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 md:gap-4 justify-center py-4">
            {values.map((v, i) => (
              <Die key={i} value={v} color={DIE_COLORS[i % DIE_COLORS.length]} spinning={spinning} />
            ))}
          </div>

          <div className="mt-6 flex items-center justify-center gap-3">
            <button data-testid={EMBED.diceRoll} onClick={roll} disabled={spinning}
              className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-8 py-3 rounded-full transition-colors disabled:opacity-40 pulse-neon">
              <Dice5 className="w-4 h-4" /> {spinning ? "Rolling…" : "Roll"}
            </button>
            <button onClick={() => setHistory([])}
              className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest border border-white/15 text-white/60 hover:text-white px-4 py-3 rounded-full">
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>

        <div className="mt-6">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// history</div>
          {history.length === 0 ? (
            <div className="text-white/40 text-sm mono">Roll to build history.</div>
          ) : (
            <div className="space-y-1.5">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-3 glass rounded-lg px-3 py-2">
                  <div className="mono text-[10px] text-white/50 min-w-[50px]">#{h.id.toString().slice(-4)}</div>
                  <div className="flex gap-1 flex-1 flex-wrap">
                    {h.values.map((v, i) => (
                      <span key={i} className="mono text-xs px-2 py-0.5 rounded bg-white/5 border border-white/10">{v}</span>
                    ))}
                  </div>
                  <div className="mono text-sm font-bold text-[#FFE300]">= {h.total}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-8 text-center">
          six neon d6 · runs entirely in browser · no install
        </div>
      </div>
    </div>
  );
}
